import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { of, Subject } from "rxjs";
import { catchError, takeUntil, tap } from "rxjs/operators";

// Services / Subjects
import { AdService } from "../../../services/ad.service";
import { AdSubjectService } from "../../../subject/ad.subject";
import { PropertyService } from "../../../services/property.service";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";
import { SegmentSubjectService } from "../../../subject/segment.subject";

// DTOs / Enums
import { AdInformationDto } from "../../../dto/ad.information.dto";
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

const STEP_KEY = 'ad';

@Component({
    selector: 'app-property-ad',
    templateUrl: './property-ad.component.html',
    styleUrls: ['./property-ad.component.scss']
})
export class PropertyAdComponent implements OnInit, OnDestroy {
    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId: number | undefined;
    segmentId: number | undefined;
    segmentLabel: string | undefined = '';

    // --------- UI / estado -------------
    loading = true;
    offers: AdInformationDto[] = [];
    registeredOffers: AdInformationDto[] = []; // ofertas ya registradas para esta propiedad
    selectedOffer: AdInformationDto | null = null;
    showConcludeButton = false;
    private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private adService: AdService,
        private adSubject: AdSubjectService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService,
        private segmentSubject: SegmentSubjectService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Paso actual
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad + stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) { this.router.navigate(['/list-pending']); return; }
        this.propertyId = this.propertyInitial.propertyId;
        this.segmentId = this.resolveSegmentId();
        this.segmentLabel = this.resolveSegmentLabel();
        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Carga de ofertas disponibles + las ya registradas (para el hub multi-oferta)
        this.loadOffers();
        this.loadRegisteredOffers();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Carga
    // -----------------------------------------------------------
    private loadOffers(): void {
        const segId = this.segmentId;
        if (!segId) { this.loading = false; return; }

        this.adService.getOffersList(segId).pipe(
            tap((data: AdInformationDto[]) => {
                // Ocultamos remate en la grilla (lo ofreces como link aparte)
                this.offers = (data || []).filter(o => o.adCode !== 'BANK_AUCTION');
                this.loading = false;

                // aplicar cache si existe
                this.applyCache();
            }),
            catchError(err => { console.error('Error al cargar ofertas', err); this.loading = false; return of([]); }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private resolveSegmentId(): number | undefined {
        return this.propertyInitial?.segmentId || this.segmentSubject.getSelectedSegment()?.segmentId;
    }

    private resolveSegmentLabel(): string {
        return this.propertyInitial?.segmentLabel
            || this.segmentSubject.getSelectedSegment()?.segmentLabel
            || 'propiedad';
    }

    private loadRegisteredOffers(): void {
        if (!this.propertyId) return;

        this.adService.getOffersListProperty(this.propertyId).pipe(
            tap((response: any[]) => {
                this.registeredOffers = Array.isArray(response) ? response : [];
                // Con ≥1 oferta ya registrada habilitamos "Concluir" y entramos en modo "agregar más".
                this.showConcludeButton = this.registeredOffers.length > 0;
                // Primera oferta: respetamos la selección guardada en cache (no auto-seleccionamos las registradas).
                if (this.registeredOffers.length === 0) this.applyCache();
            }),
            catchError(err => { console.error('Error al obtener anuncios de la propiedad', err); return of(null); }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    isRegistered(offer: AdInformationDto): boolean {
        return this.registeredOffers.some(o => o.adId === offer.adId);
    }

    /** Con ≥1 oferta ya registrada, las siguientes NO deben re-avanzar la máquina de etapas. */
    get isAdditionalOffer(): boolean {
        return this.registeredOffers.length > 0;
    }

    // -----------------------------------------------------------
    // Cache local (FlowState)
    // -----------------------------------------------------------
    private persistToFlowState(): void {
        this.flowState.setStepData(STEP_KEY, {
            selectedAdId: this.selectedOffer?.adId ?? null,
            selectedAdCode: this.selectedOffer?.adCode ?? null,
            selectedAdLabel: this.selectedOffer?.adLabel ?? null,
            isAdditionalOffer: this.isAdditionalOffer, // /price lo usa para NO re-avanzar la etapa
            selectedIsRegistered: this.selectedOffer ? this.isRegistered(this.selectedOffer) : false // PUT vs POST en /price
        });
    }

    private applyCache(): void {
        const cached = this.flowState.getStepData(STEP_KEY);
        const id: number | null = cached?.selectedAdId ?? null;
        if (!id) return;

        // Sólo aplica si aún no hay selección
        if (!this.selectedOffer) {
            const match = this.offers.find(o => o.adId === id);
            if (match) {
                this.selectedOffer = match;
                this.adSubject.setSelectedOffer?.(match);
            }
        }
    }

    // -----------------------------------------------------------
    // UI
    // -----------------------------------------------------------
    selectOffer(offer: AdInformationDto) {
        this.selectedOffer = offer;
        this.adSubject.setSelectedOffer?.(offer);
        this.persistToFlowState();
    }

    /**
     * Maneja la selección del Remate Bancario manualmente
     */
    selectBankAuction() {
        const bank: AdInformationDto = {
            adId: 4,
            adCode: "BANK_AUCTION",
            adLabel: "Remate Bancario",
            adDateLabel: "Fecha disponible",
            description: "Publicar como remate bancario"
        };
        this.selectedOffer = bank;
        this.adSubject.setSelectedOffer?.(bank);
        this.persistToFlowState();
    }

    /**
     * Verifica si la oferta está seleccionada
     */
    isSelected(offer: AdInformationDto): boolean {
        return this.selectedOffer?.adId === offer.adId;
    }

    /**
     * Devuelve la clase de icono correspondiente según el tipo de oferta
     */
    getIconClassForOffer(adLabel: string): string {
        switch (adLabel) {
            case 'Venta':
                return 'custom-color-blue';
            case 'Alquiler':
                return 'custom-color-yellow';
            case 'Anticrético':
                return 'custom-color-light-blue';
            default:
                return 'custom-color-default';
        }
    }

    concludeRegistration(): void {
        if (!this.propertyId) { this.router.navigate(['/review']); return; }
        // Marca la propiedad COMPLETED_PROP / TO_PUBLISH (la hace visible) y muestra el review.
        this.propertyService.concludeRegistration(this.propertyId).pipe(
            catchError((err) => { console.error('Error al concluir registro', err); return of(null); }),
            takeUntil(this.destroy$)
        ).subscribe(() => this.router.navigate(['/review']));
    }

    // -----------------------------------------------------------
    // Botonera
    // -----------------------------------------------------------
    handleNextStep(): void {
        if (!this.selectedOffer) {
            // No bloqueamos por toast/alert aquí para mantener UX; puedes mostrar un mensaje
            return;
        }

        // Marcamos stage local (la persistencia POST/PUT de la oferta con precio/fecha se hace en 'price')
        this.flowState.setRecordStage(RecordStageEnum.AD);
        this.persistToFlowState();

        // Oferta adicional (ya hay ≥1 registrada): la propiedad pasó la etapa AD → no la re-avanzamos.
        if (!this.isAdditionalOffer && this.shouldAdvanceStage()) {
            const body = { id: this.propertyId };
            this.propertyService.nextStageProperty(body).pipe(
                catchError((err) => { console.error('nextStage error', err); return of(null); }),
                takeUntil(this.destroy$)
            ).subscribe(() => this.router.navigate([this.nextUrl]));
            return;
        }

        this.router.navigate([this.nextUrl]);
    }

    handleSaveAndExit(): void {
        this.persistToFlowState();
        this.router.navigate(['/list-pending']);
    }

    handlePreviousStep(): void {
        this.router.navigate([this.previousUrl]);
    }

    // Política de avance:
    // - CREATE → avanza
    // - EDIT → no avanza
    // - RESUME → avanza sólo si estás en un paso posterior al recordStage
    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx;
    }
}
